import * as React from "react";
import styled from "styled-components";
import { EditableUpdate, Update, ValueType } from ".";
import Checkbox, { CheckboxStates } from "../common/Checkbox";

interface Props {
  articles: EditableUpdate[];
  onSetFieldOverwrite: (
    update: EditableUpdate,
    newOverwriteFlags: ValueType
  ) => void;
}

const Table = styled.table`
  & thead tr {
    & > *:nth-child(2) {
      width: 300px;
    }
  }

  // Right align text in the first column
  & > * > tr > * {
    white-space: pre-wrap;

    &:nth-child(1) {
      text-align: right;
      font-weight: bold;
      width: 150px; // Fit column to content size
    }

    &:nth-child(4) {
      text-align: right;
      width: 130px;
    }

    &:last-child {
      padding-right: 10px;
    }
  }
`;

interface RowProps extends React.PropsWithChildren {
  update: EditableUpdate;
  valueType: ValueType;
  onSetFieldOverwrite: (
    update: EditableUpdate,
    newOverwriteFlags: ValueType
  ) => void;
}

const Row = ({
  children,
  update,
  valueType,
  onSetFieldOverwrite,
}: RowProps) => {
  function hasChanges(update: Update, type: ValueType): boolean {
    return (update.changes & type) == type;
  }

  function hasConflicts(update: Update, type: ValueType): boolean {
    return (update.conflicts & type) == type;
  }

  function hasOverwriteFlag(update: EditableUpdate, type: ValueType): boolean {
    return (update.overwrites & type) != 0;
  }

  function onFieldOverwriteChanged(
    update: EditableUpdate,
    type: ValueType,
    overwrite: boolean
  ) {
    let flags = update.overwrites;

    if (overwrite) {
      flags |= type;
    } else {
      flags &= ~type;
    }

    onSetFieldOverwrite(update, flags);
  }

  if (!hasChanges(update, valueType)) {
    return null;
  }

  return (
    <tr>
      {children}
      {hasConflicts(update, valueType) && (
        <td>
          <input
            type="checkbox"
            checked={hasOverwriteFlag(update, valueType)}
            onChange={(ev) =>
              onFieldOverwriteChanged(update, valueType, ev.target.checked)
            }
          />
        </td>
      )}
    </tr>
  );
};

const ExistingArticlesList = ({ articles, onSetFieldOverwrite }: Props) => {
  const onOverwriteAllChanged = (
    update: EditableUpdate,
    ev: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (ev.target.checked) {
      onSetFieldOverwrite(update, update.conflicts);
    } else {
      onSetFieldOverwrite(update, ValueType.None);
    }
  };

  const getOverwriteAllState = (update: EditableUpdate): CheckboxStates => {
    if ((update.overwrites & update.conflicts) == update.conflicts) {
      return CheckboxStates.Checked;
    } else if (update.overwrites == 0) {
      return CheckboxStates.Unchecked;
    }

    return CheckboxStates.Indeterminate;
  };

  return (
    <div>
      {!articles.length && <p>Inga artiklar med nya värden hittades.</p>}
      {articles.map((update) => (
        <div key={update.new.articleNo}>
          <h4>
            {update.current.articleNo} - {update.current.name}
          </h4>
          <Table className="table-small dataTable dtr-inline">
            <thead>
              <tr>
                <th>Fält</th>
                <th>Nuvarande värde</th>
                <th>Nytt värde</th>
                {update.conflicts != ValueType.None && (
                  <th>
                    <label>
                      <strong>Skriv över</strong>
                      &nbsp;
                      <Checkbox
                        onChange={(ev) => onOverwriteAllChanged(update, ev)}
                        state={getOverwriteAllState(update)}
                      />
                    </label>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              <Row
                update={update}
                valueType={ValueType.ArticleNo}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Artikelnummer</td>
                <td>{update.current.articleNo}</td>
                <td>{update.new.articleNo}</td>
              </Row>
              <Row
                update={update}
                valueType={ValueType.ArticleGroup}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Artikelgrupp</td>
                <td>{update.current.articleGroup}</td>
                <td>{update.new.articleGroup}</td>
              </Row>
              <Row
                update={update}
                valueType={ValueType.Name}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Namn</td>
                <td>{update.current.name}</td>
                <td>{update.new.name}</td>
              </Row>
              <Row
                update={update}
                valueType={ValueType.Unit}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Enhet</td>
                <td>{update.current.unit}</td>
                <td>{update.new.unit}</td>
              </Row>
              <Row
                update={update}
                valueType={ValueType.Price}
                onSetFieldOverwrite={onSetFieldOverwrite}
              >
                <td>Pris</td>
                <td>{update.current.price}</td>
                <td>{update.new.price}</td>
              </Row>
            </tbody>
          </Table>
        </div>
      ))}
    </div>
  );
};

export default ExistingArticlesList;
